import { Box, Button, Theme, Typography } from "@outschool/backpack";
import { SELF_PACED_MAX_DELAY_START_TIME } from "@outschool/business-rules";
import { getFundingProgramWaitPeriod } from "@outschool/funding-programs-shared";
import {
  ActivityEnrollQueryQuery,
  ChildFragmentFragment,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { dayjs, fullDurationStringWithoutTimes } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useFundingProgramAbbr } from "@outschool/ui-auth";
import {
  AvailableDateSelector,
  IconBullet,
  useSelfPacedIconInfo,
} from "@outschool/ui-components-website";
import { useIsMobile } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";

import { useTimeZone } from "../../providers/TimeZoneProvider";
import { AvailableTimesFooterMobile } from "../detailPage/activityEnrollableTimes/ActivityEnrollableTimes";
import LearnerSwitcher, {
  useGetLearners,
} from "../detailPage/subcomponents/LearnerSwitcher";
import {
  VirginiaLAGAdjustmentAlert,
  useVirginiaSelfPacedAdjustment,
} from "./VirginiaLAGAdjustmentAlert";

type EnrollmentFormNonNullable = NonNullable<
  ActivityEnrollQueryQuery["enrollmentForm"]
>;

const TRACKED_COMPONENT_NAME = "SelfPacedChooseStartTime";

export default function SelfPacedChooseStartTime({
  activity,
  selectedStartTime,
  onSelectStartTime,
}: {
  activity: EnrollmentFormNonNullable["activity"];
  selectedStartTime: Date;
  onSelectStartTime: (startTime: Date) => void;
}) {
  const { timeZone } = useTimeZone();
  const trackEvent = useTrackEvent();
  const viewTracked = React.useRef(false);
  const { t } = useTranslation(
    "client\\components\\enrolling\\SelfPacedChooseStartTime",
  );
  const [selfPacedStartTime, setSelfPacedStartTime] =
    React.useState(selectedStartTime);
  const selfPacedIconInfo = useSelfPacedIconInfo(activity);
  const { hasWeeksOfFeedbackAdjustment, feedbackTimeText, endDate } =
    useVirginiaSelfPacedAdjustment({ activity, selfPacedStartTime });
  const esaState = useFundingProgramAbbr();

  const startOfDateString = React.useCallback(
    (date: Date) => dayjs(date).tz(timeZone).startOf("date").toISOString(),
    [timeZone],
  );

  let SPA_CUTOFF_START_TIME = SELF_PACED_MAX_DELAY_START_TIME;
  const minimumAvailableDay = getFundingProgramWaitPeriod(esaState);
  const availableDates = _.range(
    minimumAvailableDay,
    SPA_CUTOFF_START_TIME,
  ).map(d => dayjs.tz(timeZone).add(d, "days").toISOString());
  const firstDate = dayjs(availableDates[0]).startOf("day").toDate();
  const lastDate = dayjs(availableDates.at(-1)).endOf("day").toDate();
  const isValid =
    firstDate <= selfPacedStartTime && selfPacedStartTime <= lastDate;

  if (!viewTracked.current) {
    viewTracked.current = true;
    trackEvent("Time Selector Viewed", {
      component: TRACKED_COMPONENT_NAME,
      selectedStartTime: selfPacedStartTime,
    });
  }

  const enrollButtonClick = () => {
    if (selfPacedStartTime) {
      onSelectStartTime(selfPacedStartTime);
    }

    trackEvent("Time Selector Enroll Clicked", {
      component: TRACKED_COMPONENT_NAME,
      selectedStartTime: selfPacedStartTime,
    });
  };

  const isMobile = useIsMobile();
  const learners = useGetLearners().map(learner => ({
    ...learner,
    date_of_birth: null,
  }));
  const [selectedLearner, setSelectedLearner] =
    React.useState<ChildFragmentFragment>(learners[0] as ChildFragmentFragment);

  return (
    <Box>
      <Typography
        variant="h3"
        emphasized
        sx={(theme: Theme) => ({
          marginBottom: 16,
          marginTop: "calc(80px - 1.5em)",
          [theme.breakpoints.up("md")]: {
            marginTop: 4,
          },
        })}
      >
        {t("Select a start date")}
      </Typography>
      <Box
        flex
        sx={(theme: Theme) => ({
          flexDirection: "column",
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
          },
          marginBottom: 32,
          gap: "1em",
        })}
      >
        {isMobile && (
          <LearnerSwitcher
            learners={learners}
            selectedLearner={selectedLearner}
            setSelectedLearner={setSelectedLearner}
          />
        )}
        <AvailableDateSelector
          today={dayjs.tz(timeZone).startOf("day").toDate()}
          availableDates={availableDates}
          selectedDate={selfPacedStartTime}
          handleDayClick={(date: Date) => setSelfPacedStartTime(date)}
          timeZone={timeZone}
          startOfDateString={startOfDateString}
          defaultMonth={selfPacedStartTime}
          handleMonthChange={(date: Date) => {
            trackEvent("Time Selector Month Changed", {
              component: TRACKED_COMPONENT_NAME,
              monthStartTime: date,
              selectedStartTime: selfPacedStartTime,
            });
          }}
        />
        <Box>
          <Typography variant="h5" emphasized sx={{ marginBottom: 16 }}>
            {t("Go at your own pace")}
          </Typography>
          <Box sx={{ marginBottom: 16 }}>
            {t("From the start date,  you’ll receive...")}
          </Box>
          <Box sx={{ display: "grid", gap: 8 }}>
            <IconBullet
              icon={selfPacedIconInfo.numberOfLessons.icon}
              text={selfPacedIconInfo.numberOfLessons.text}
            />
            <IconBullet
              icon={selfPacedIconInfo.weeksOfFeedback.icon}
              text={feedbackTimeText}
            />
            <IconBullet
              icon={selfPacedIconInfo.accessToContent.icon}
              text={selfPacedIconInfo.accessToContent.text}
            />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          paddingTop: 16,
        }}
      >
        {hasWeeksOfFeedbackAdjustment && (
          <VirginiaLAGAdjustmentAlert alertSx={{ marginBottom: "1em" }} />
        )}
        {isMobile ? (
          <AvailableTimesFooterMobile
            hasSelectedTime={!!selfPacedStartTime}
            continueButtonText={t`Enroll`}
            handleContinueClick={enrollButtonClick}
          />
        ) : (
          <Box
            flex
            sx={(theme: Theme) => ({
              [theme.breakpoints.up("md")]: {
                flexDirection: "row",
              },
              justifyContent: "space-between",
              alignItems: "center",
            })}
          >
            <Box>
              {selfPacedStartTime &&
                fullDurationStringWithoutTimes(
                  selfPacedStartTime,
                  endDate,
                  timeZone,
                )}
            </Box>
            <Button
              onClick={enrollButtonClick}
              disabled={!isValid}
              variant="contained"
              color="primary"
            >
              {t("Next")}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
